import { z } from 'zod';

import { MESSAGE } from '@/lib/constants';

// ─── Line item row ───────────────────────────────────────────────────────────
// `amount` is a string off a native <input>; the caller maps rows to the API's
// `QuoteLineItem[]` via `toLineItems`.

export const lineItemRowSchema = z
  .object({
    description: z
      .string()
      .trim()
      .min(1, { message: MESSAGE.REQUIRED_FIELD })
      .max(200, { message: MESSAGE.charMax(200) }),
    amount: z.string().trim(),
  })
  .superRefine((val, ctx) => {
    const n = Number(val.amount);
    if (val.amount === '' || !Number.isFinite(n) || n < 0) {
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Enter a valid amount' });
    }
  });

export type LineItemRow = z.infer<typeof lineItemRowSchema>;

// ─── Shared quote fields (spread into add + edit) ────────────────────────────
// `totalAmount` is only validated when `manualTotal` is on — otherwise it's
// derived from the line items on submit.

export const quoteFieldsSchema = {
  lineItems: z.array(lineItemRowSchema).max(100),
  notes: z.string().trim().max(5000, { message: MESSAGE.charMax(5000) }).optional(),
  manualTotal: z.boolean(),
  totalAmount: z.string().trim(),
};

// Shape shared by `AddQuoteInput` and `EditQuoteInput` — the `control` type the
// `LineItemsField` sub-form binds to (RHF `Control` is invariant, so callers
// cast their form control to this).
export type QuoteFieldsValues = {
  lineItems: LineItemRow[];
  notes?: string;
  manualTotal: boolean;
  totalAmount: string;
};

export function refineQuoteTotal(
  val: { manualTotal: boolean; totalAmount: string; lineItems: LineItemRow[] },
  ctx: z.RefinementCtx,
) {
  if (val.manualTotal) {
    const n = Number(val.totalAmount);
    if (val.totalAmount === '' || !Number.isFinite(n) || n <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['totalAmount'],
        message: 'Enter a valid amount',
      });
    }
    return;
  }

  // Auto total: needs at least one line item that sums to > 0.
  const sum = val.lineItems.reduce((acc, row) => {
    const n = Number(row.amount);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
  if (sum <= 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['lineItems'],
      message: 'Add a line item, or set the total manually',
    });
  }
}

// ─── Add quote (business-wide page — needs a job picker) ──────────────────────

export const addQuoteSchema = z
  .object({
    jobId: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    ...quoteFieldsSchema,
  })
  .superRefine(refineQuoteTotal);

export type AddQuoteInput = z.infer<typeof addQuoteSchema>;

// ─── Edit quote (detail modal) ───────────────────────────────────────────────

export const editQuoteSchema = z.object(quoteFieldsSchema).superRefine(refineQuoteTotal);

export type EditQuoteInput = z.infer<typeof editQuoteSchema>;
